import { createContext, useState, useEffect, useContext } from 'react';
import yup from 'utils/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { BuyerChatRoomQuery } from 'interfaceAdapter/queries/BuyerChatRoom';
import { BuyerChatRepository } from 'interfaceAdapter/repositories/BuyerChat';
import { ResponseMeetingRequestForBuyerRepository } from 'interfaceAdapter/repositories/ResponseMeetingRequestForBuyer';
import { useModal } from 'ui/components/screens/modals/useModal';
import { IChatRoom } from 'domain/entities/ChatRoom/ChatRoom';
import { IChat } from 'domain/entities/Chat/Chat';
import { useLoading } from 'application/contexts/useLoading';
import { useParams } from 'react-router-dom';

type BuyerChatContext = {
    loading: boolean;
    showSnackbar: boolean;
    snackbarMessage: string;
    shwAgreementDialog: boolean;
    unreadIds: number[];
    blockAgentErrors: any;
    setShwAgreementDialog: (value: boolean) => void;
    setShowSnackbar: (value: boolean) => void;
    chatRooms: IChatRoom[];
    filteredChatRooms: IChatRoom[];
    selectedChatRoom: IChatRoom | null;
    setSelectedChatRoom: (chatRoom: IChatRoom) => void;
    message: string;
    setMessage: (message: string) => void;
    keyword: string;
    setKeyword: (keyword: string) => void;
    sendMessage: () => void;
    chatMessages: IChat[];
    setMeetingDate: (meetingDate: string, closeModal: () => void) => void;
    confirmMeetingDate: () => void;
    declineMeetingDate: () => void;
    fetchData: () => void;
    openArchiveAgentModal: () => void;
    createArchiveAgentModal: any;
    blockAgent: () => void;
    setArchiveAgentValue: (value: string) => void;
};

const defaultContext: BuyerChatContext = {
    loading: false,
    showSnackbar: false,
    snackbarMessage: '',
    shwAgreementDialog: false,
    unreadIds: [],
    blockAgentErrors: {},
    setShwAgreementDialog: () => {},
    setShowSnackbar: () => {},
    chatRooms: [],
    filteredChatRooms: [],
    selectedChatRoom: null,
    setSelectedChatRoom: () => {},
    message: '',
    setMessage: () => {},
    keyword: '',
    setKeyword: () => {},
    sendMessage: () => {},
    chatMessages: [],
    setMeetingDate: () => {},
    confirmMeetingDate: () => {},
    declineMeetingDate: () => {},
    fetchData: () => {},
    openArchiveAgentModal: () => {},
    createArchiveAgentModal: () => {},
    blockAgent: () => {},
    setArchiveAgentValue: (value: string) => {},
};

export const buyerChatContext = createContext<BuyerChatContext>(defaultContext);

const schema = yup
    .object({
        message: yup.string().max(1000).required(),
    })
    .required();

type archiveAgentForm = yup.InferType<typeof schema>;

export const useBuyerChat = (): BuyerChatContext => {
    const diContainer = useContext(diContainerContext);
    const buyerChatRoomQuery = diContainer.resolve(BuyerChatRoomQuery);
    const buyerChatRepository = diContainer.resolve(BuyerChatRepository);
    const meetingRequestRepository = diContainer.resolve(ResponseMeetingRequestForBuyerRepository);
    const { loading, setLoading } = useLoading();
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [shwAgreementDialog, setShwAgreementDialog] = useState(false);
    const [chatRooms, setChatRooms] = useState<IChatRoom[]>([]);
    const [unreadIds, setUnreadIds] = useState<number[]>([]);
    const [filteredChatRooms, setFilteredChatRooms] = useState<IChatRoom[]>([]);
    const [selectedChatRoom, setSelectedChatRoom] = useState<IChatRoom | null>(null);
    const [keyword, setKeyword] = useState('');
    const [message, setMessage] = useState('');
    const [chatMessages, setChatMessages] = useState<IChat[]>([]);
    const params = useParams();
    const archiveAgentModal = useModal();
    const {
        openModal: openArchiveAgentModal,
        createModal: createArchiveAgentModal,
        closeModal: closeArchiveAgentModal,
    } = archiveAgentModal;

    const {
        handleSubmit,
        setValue,
        formState: { errors: blockAgentErrors },
    } = useForm<archiveAgentForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            message: '',
        },
    });

    const fetchData = async () => {
        const data = await buyerChatRoomQuery.fetchRoomList();
        setChatRooms(data);

        const unreadIds = data.filter((chatRoom) => chatRoom.unread).map((chatRoom) => chatRoom.id);
        setUnreadIds(unreadIds);
        setLoading(false);
    };

    useEffect(() => {
        if (keyword.trim()) {
            const filtered = chatRooms.filter((chatRoom) =>
                chatRoom.match.agent.name.toLowerCase().includes(keyword.toLowerCase()),
            );
            setFilteredChatRooms(filtered);
        } else {
            setFilteredChatRooms(chatRooms);
        }
    }, [keyword, chatRooms]);

    // チャットメッセージ送信
    const sendMessage = async () => {
        if (!selectedChatRoom) return;
        setLoading(true);
        try {
            await buyerChatRepository.send(selectedChatRoom.id, message);
            setMessage('');
            const data = await buyerChatRoomQuery.fetchMessages(selectedChatRoom.id);
            setChatMessages(data);
        } finally {
            setLoading(false);
        }
    };

    // 面談日を設定する
    const setMeetingDate = async (meetingDate: string, closeModal: () => void) => {
        if (!selectedChatRoom) return;
        setLoading(true);
        try {
            await buyerChatRepository.setMeetingDate(selectedChatRoom.match.id, meetingDate);
            const data = await buyerChatRoomQuery.fetchRoomList();
            setChatRooms(data);
            closeModal();
            setSnackbarMessage('面談日を設定しました');
            setShowSnackbar(true);
        } finally {
            setLoading(false);
        }
    };

    // 面談日リクエストを承諾する
    const confirmMeetingDate = async () => {
        if (!selectedChatRoom) return;
        setLoading(true);
        try {
            await buyerChatRepository.confirmMeetingDate(selectedChatRoom.match.id);
            const data = await buyerChatRoomQuery.fetchRoomList();
            setChatRooms(data);
            setSelectedChatRoom((prev) => {
                const updatedChatRoom = data.find((room) => room.id === prev?.id);
                return updatedChatRoom ? updatedChatRoom : prev;
            });
            setShowSnackbar(true);
        } finally {
            setLoading(false);
        }
    };

    // 面談日リクエストを拒否する
    const declineMeetingDate = async () => {
        if (!selectedChatRoom) return;
        setLoading(true);
        try {
            await buyerChatRepository.declineMeetingDate(selectedChatRoom.match.id);
            const data = await buyerChatRoomQuery.fetchRoomList();
            setChatRooms(data);
            setSelectedChatRoom((prev) => {
                const updatedChatRoom = data.find((room) => room.id === prev?.id);
                return updatedChatRoom ? updatedChatRoom : prev;
            });
            setShowSnackbar(true);
        } finally {
            setLoading(false);
        }
    };

    const setArchiveAgentValue = (value: string) => {
        setValue('message', value);
    };

    const blockAgent = handleSubmit(async ({ message }) => {
        if (!selectedChatRoom) return;
        setLoading(true);
        try {
            await meetingRequestRepository.blockAgent(selectedChatRoom.match.id, message);
        } finally {
            setLoading(false);
        }
        setSnackbarMessage('仲介会社を通報しました');
        setShowSnackbar(true);
        closeArchiveAgentModal();
        setTimeout(() => {
            // refetch させたいのでリロード
            window.location.href = `/buyer/message/${selectedChatRoom.id}`;
        }, 1500);
    });

    useEffect(() => {
        if (params.chat_room_id === undefined) return;
        const chatRoomId = Number(params.chat_room_id);
        if (!chatRoomId) return;
        const selected = chatRooms.find((chatRoom) => chatRoom.id === chatRoomId);
        if (selected) {
            setSelectedChatRoom(selected);
        }
    }, [params, chatRooms]);

    useEffect(() => {
        if (!selectedChatRoom) return;
        const fetchMessages = async () => {
            const data = await buyerChatRoomQuery.fetchMessages(selectedChatRoom.id);
            setChatMessages(data);
            // 既読にする
            setUnreadIds((prev) => prev.filter((id) => id !== selectedChatRoom.id));
        };
        fetchMessages();
    }, [selectedChatRoom]);

    return {
        loading,
        showSnackbar,
        snackbarMessage,
        shwAgreementDialog,
        unreadIds,
        blockAgentErrors,
        setShwAgreementDialog,
        setShowSnackbar,
        chatRooms,
        filteredChatRooms,
        selectedChatRoom,
        setSelectedChatRoom,
        message,
        setMessage,
        keyword,
        setKeyword,
        sendMessage,
        chatMessages,
        setMeetingDate,
        confirmMeetingDate,
        declineMeetingDate,
        fetchData,
        openArchiveAgentModal,
        createArchiveAgentModal,
        blockAgent,
        setArchiveAgentValue,
    };
};
